import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Capacitor } from '@capacitor/core';
import { v4 } from 'uuid';
import { AuthService } from '../auth.service';
import { Branch, EnvironmentService } from '../environment/environment.service';

/**
 * - Meta data for Produktly.identify
 * - Used for user segmantation
 */
export type ProduktlyMetaData = {
  id: string;
  userMail?: string;
  tenantId?: string;
  tenantName?: string;
  product: string;
  appVersion: string;
  appEnvironment: string;
  appBuildNumber: string;
  appPlatform: string;
  authenticated: string; // must be string because Produktly bugs when segmenting users by boolean
};

@Injectable({
  providedIn: 'root',
})
export class ProduktlyService {
  private readonly metaDataTemplate: ProduktlyMetaData = {
    id: 'unauthenticated',
    product: 'alberta',
    authenticated: 'false',
    appVersion: this._environmentService.version,
    appEnvironment: this._environmentService.branch,
    appBuildNumber: this._environmentService.buildNumber,
    appPlatform: Capacitor.getPlatform(),
  };

  private _isReady = false;

  constructor(
    private readonly _authService: AuthService,
    private readonly _environmentService: EnvironmentService,
    private readonly _router: Router
  ) {}

  public async init(): Promise<void> {
    this._setupProduktly();
    const metaData = { ...this.metaDataTemplate };
    if (this._authService.authentication.isAuth) {
      let userId = this._authService.authentication.account._id;
      if (this._environmentService.branch === Branch.dev) {
        if (localStorage.getItem('alberta_produktly_debug')) {
          // It is not possible to reset a finished tour for a user, so we have to create a new user for debugging
          userId += v4();
        }
      }
      metaData.id = userId;
      metaData.userMail = this._authService.authentication.account.email;
      metaData.tenantId = this._authService.authentication.account.tenantId;
      metaData.tenantName = this._authService.authentication.account.organization.tenantId;
      metaData.authenticated = 'true';
      (window as any).Produktly?.identifyUser(userId, metaData);
    } else {
      // if the user is not logged in, we use a uuid to identify progress etc of the user
      let preExistingUnauthenticatedUserId = localStorage.getItem('alberta_produktly_anonymous_id');
      if (!preExistingUnauthenticatedUserId) {
        preExistingUnauthenticatedUserId = v4();
        localStorage.setItem('alberta_produktly_anonymous_id', preExistingUnauthenticatedUserId);
      }
      (window as any).Produktly?.identifyUser(preExistingUnauthenticatedUserId, this.metaDataTemplate);
    }
  }

  public clearProduktlyUser() {
    // ugly workaround to "clear" user identification after logout
    // there's currently no sdk function to do this...
    localStorage.removeItem('produktly_external_user_id');
    localStorage.removeItem('produktly_user_id');
  }

  private _setupProduktly() {
    if (this._isReady) {
      return;
    }
    this._isReady = true;
    (window as any).Produktly.configure({
      redirect: async (url: string) => {
        // Produktly provides the url the user should be redirected to, but we need to navigate to it
        // Provided through Produktly as path, e.g. /article/123
        await this._router.navigateByUrl(url);
      },
    });
  }
}
